import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { SpellName } from '@dndbeyond/character-components/es';
import {
    AnySimpleDataType,
    Hack__BaseCharClass,
    rulesEngineSelectors,
    characterActions,
    ClassUtils,
    DataOriginRefData,
    EntityUtils,
    EntityValueLookup,
    FormatUtils,
    RuleData,
    Spell,
    SpellCasterInfo,
    SpellUtils,
    ValueUtils,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import SpellDetail from '../../../components/SpellDetail';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneUtils } from '../../../utils';
import { PaneIdentifiersClassSpell } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    spells: Array<Spell>;
    spellCasterInfo: SpellCasterInfo;
    castLevel: number;
    ruleData: RuleData;
    entityValueLookup: EntityValueLookup;
    dataOriginRefData: DataOriginRefData;
    identifiers: PaneIdentifiersClassSpell | null;
    isReadonly: boolean;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
interface State {
    spell: Spell | null;
}
class ClassSpellPane extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { spells, identifiers } = this.props;

        if (spells !== prevProps.spells || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { spells, identifiers } = props;

        let foundSpell: Spell | null | undefined = null;
        if (identifiers !== null) {
            foundSpell = spells.find(
                (spell) =>
                    identifiers.spellId === SpellUtils.getMappingId(spell) &&
                    identifiers.classId ===
                        ClassUtils.getMappingId(SpellUtils.getDataOrigin(spell).primary as Hack__BaseCharClass),
            );
        }

        return {
            spell: foundSpell ? foundSpell : null,
        };
    };

    handleCustomDataUpdate = (key: number, value: AnySimpleDataType, source: string | null): void => {
        const { spell } = this.state;
        const { dispatch } = this.props;

        if (spell === null) {
            return;
        }

        let mappingId = SpellUtils.getMappingId(spell);
        let mappingEntityTypeId = SpellUtils.getMappingEntityTypeId(spell);
        let [contextId, contextTypeId] = SpellUtils.deriveExpandedContextIds(spell);

        dispatch(
            characterActions.valueSet(
                key,
                value,
                source,
                ValueUtils.hack__toString(mappingId),
                ValueUtils.hack__toString(mappingEntityTypeId),
                ValueUtils.hack__toString(contextId),
                ValueUtils.hack__toString(contextTypeId),
            ),
        );
    };

    handleRemoveCustomizations = (): void => {
        const { spell } = this.state;
        const { dispatch } = this.props;

        if (spell) {
            let mappingId = SpellUtils.getMappingId(spell);
            let mappingEntityTypeId = SpellUtils.getMappingEntityTypeId(spell);
            if (mappingId !== null && mappingEntityTypeId !== null) {
                let [contextId, contextTypeId] = SpellUtils.deriveExpandedContextIds(spell);

                dispatch(
                    characterActions.spellCustomizationsDelete(
                        mappingId,
                        mappingEntityTypeId,
                        contextId,
                        contextTypeId,
                    ),
                );
            }
        }
    };

    handleParentClick = (): void => {
        const { spell } = this.state;
        const { dispatch, dataOriginRefData } = this.props;

        if (spell === null) {
            return;
        }

        let dataOrigin = SpellUtils.getDataOrigin(spell);
        let component = PaneUtils.getDataOriginComponentInfo(dataOrigin);

        let expandedDataOriginRef = SpellUtils.getExpandedDataOriginRef(spell);
        if (expandedDataOriginRef !== null) {
            component = PaneUtils.getDataOriginRefComponentInfo(expandedDataOriginRef, dataOriginRefData);
        }

        if (component.type !== PaneComponentEnum.ERROR_404) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    render() {
        const { spell } = this.state;
        const {
            spellCasterInfo,
            castLevel,
            ruleData,
            entityValueLookup,
            dataOriginRefData,
            isReadonly,
            proficiencyBonus,
            theme,
        } = this.props;

        if (spell === null) {
            return <PaneInitFailureContent />;
        }

        let school = SpellUtils.getSchool(spell);
        let schoolSlug = FormatUtils.slugify(school);

        const spellDataOrigin = SpellUtils.getDataOrigin(spell);

        let parentNode: React.ReactNode = EntityUtils.getDataOriginName(spellDataOrigin);
        let expandedDataOriginRef = SpellUtils.getExpandedDataOriginRef(spell);
        if (expandedDataOriginRef !== null) {
            parentNode = (
                <React.Fragment>
                    {parentNode} &bull; {EntityUtils.getDataOriginRefName(expandedDataOriginRef, dataOriginRefData)}
                </React.Fragment>
            );
        }

        return (
            <div className="ct-spell-pane">
                <SidebarHeader
                    parent={parentNode}
                    onParentClick={expandedDataOriginRef !== null ? this.handleParentClick : undefined}
                    preview={
                        <div
                            className={`ct-spell-pane__heading-preview ct-spell-pane__heading-preview--school-${schoolSlug}`}
                        />
                    }
                >
                    <SpellName
                        theme={theme}
                        spell={spell}
                        showSpellLevel={false}
                        dataOriginRefData={dataOriginRefData}
                    />
                </SidebarHeader>
                <SpellDetail
                    theme={theme}
                    key={SpellUtils.getUniqueKey(spell)}
                    spell={spell}
                    spellCasterInfo={spellCasterInfo}
                    ruleData={ruleData}
                    initialCastLevel={castLevel}
                    showActions={false}
                    onCustomDataUpdate={this.handleCustomDataUpdate}
                    onCustomizationsRemove={this.handleRemoveCustomizations}
                    entityValueLookup={entityValueLookup}
                    isReadonly={isReadonly}
                    proficiencyBonus={proficiencyBonus}
                />
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        spells: rulesEngineSelectors.getClassSpells(state),
        spellCasterInfo: rulesEngineSelectors.getSpellCasterInfo(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        entityValueLookup: rulesEngineSelectors.getCharacterValueLookupByEntity(state),
        dataOriginRefData: rulesEngineSelectors.getDataOriginRefData(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(ClassSpellPane);
